import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { supabase } from '../../lib/supabase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie, faArrowRightFromBracket,
  faGear, faBuilding, faClipboardCheck, faNetworkWired, faDollarSign,
  faServer, faCalendarDays, faArrowTrendUp, faInbox, faFeather, faRepeat, faPenNib,
  faEnvelope, faLightbulb, faHammer
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import Avatar from '../ui/Avatar';
import { uniqueChannelName } from '../../lib/realtime';
import { countPending, subscribeEmailItems } from '../../lib/inbox';
import { countOpenFeedback } from '../../lib/clientFeedback';

interface NavItem {
  id: string;
  label: string;
  icon: IconDefinition;
  to: string;
  match: (path: string) => boolean;
  show: boolean;
  badge?: string | null;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

export default function Sidebar() {
  const { user, logout } = useUser();
  const location = useLocation();

  const [isExpanded, setIsExpanded] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasFullAccess, setHasFullAccess] = useState(false);
  const [taskCount, setTaskCount] = useState(0);
  const [commsCount, setCommsCount] = useState(0);
  const [emailCount, setEmailCount] = useState(0);

  // Check if user is admin by querying the public.users table
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('users')
          .select('role, avatar')
          .eq('id', user.id)
          .single();

        if (!error && data) {
          const normalizedRole = data.role?.trim?.().toLowerCase?.() || '';
          setIsAdmin(normalizedRole === 'admin');
          setHasFullAccess(normalizedRole === 'admin' || normalizedRole === 'developer' || normalizedRole === 'agency_owner');
          if (data.avatar) setAvatarUrl(data.avatar);
        }
      } catch (err) {
        console.error('Error checking admin status:', err);
      }
    };

    checkAdminStatus();
  }, [user]);

  // Fetch website tasks count for Private Network badge
  useEffect(() => {
    const fetchTaskCount = async () => {
      try {
        const { count, error } = await supabase
          .from('website_tasks')
          .select('*', { count: 'exact', head: true });

        if (error) throw error;
        setTaskCount(count || 0);
      } catch (err) {
        console.error('Error fetching task count:', err);
        setTaskCount(0);
      }
    };

    fetchTaskCount();

    // Set up realtime subscription to update count when tasks change
    const tasksSubscription = supabase
      .channel(uniqueChannelName('website_tasks_channel'))
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'website_tasks' },
        fetchTaskCount
      )
      .subscribe();

    return () => {
      supabase.removeChannel(tasksSubscription);
    };
  }, []);

  // Fetch open client comms count for the Client Comms badge
  useEffect(() => {
    const fetchCommsCount = async () => {
      try {
        // View = open cards on active boards MINUS cards with an active linked
        // task (those live in the Assigned tab, not the Open inbox)
        // ...plus client feedback batches, which share the Open feed (same rule as the tab)
        const [{ count, error }, feedback] = await Promise.all([
          supabase.from('comms_open_cards').select('*', { count: 'exact', head: true }),
          countOpenFeedback(),
        ]);

        if (error) throw error;
        setCommsCount((count || 0) + feedback);
      } catch (err) {
        console.error('Error fetching comms count:', err);
        setCommsCount(0);
      }
    };

    fetchCommsCount();

    const commsSubscription = supabase
      .channel(uniqueChannelName('comms_items_channel'))
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'comms_items' },
        fetchCommsCount
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'comms_boards' },
        fetchCommsCount
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'client_tasks' },
        fetchCommsCount // assigning/submitting/archiving a linked task moves cards
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'comms_task_links' },
        fetchCommsCount // attaching/detaching an event moves cards
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'client_feedback_batches' },
        fetchCommsCount
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'client_feedback_items' },
        fetchCommsCount // notes settling or withdrawn move a batch to Cleared
      )
      .subscribe();

    return () => {
      supabase.removeChannel(commsSubscription);
    };
  }, []);

  // Fetch pending email reviews for the Inbox badge
  useEffect(() => {
    const fetchEmailCount = async () => {
      try {
        setEmailCount(await countPending());
      } catch (err) {
        console.error('Error fetching email review count:', err);
        setEmailCount(0);
      }
    };

    fetchEmailCount();

    return subscribeEmailItems(fetchEmailCount);
  }, []);

  // Auto collapse the sidebar when navigating on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setIsExpanded(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (window.innerWidth < 1024) {
      setIsExpanded(false);
    }

    logout();
  };

  const groups: NavGroup[] = [
    {
      label: 'Business',
      items: [
        { id: 'clients', label: 'Clients', icon: faBuilding, to: '/clients', match: (p) => p === '/clients' || p.startsWith('/clients/'), show: hasFullAccess },
        { id: 'bookings', label: 'Bookings', icon: faCalendarDays, to: '/bookings', match: (p) => p === '/bookings' || p.startsWith('/bookings/'), show: hasFullAccess },
        { id: 'sales', label: 'Sales', icon: faArrowTrendUp, to: '/sales', match: (p) => p === '/sales' || p.startsWith('/sales/'), show: isAdmin },
        { id: 'financials', label: 'Financials', icon: faDollarSign, to: '/financials', match: (p) => p === '/financials', show: isAdmin },
      ],
    },
    {
      label: 'Communication',
      items: [
        { id: 'inbox', label: 'Inbox', icon: faEnvelope, to: '/inbox', match: (p) => p === '/inbox', show: hasFullAccess, badge: emailCount > 0 ? (emailCount > 99 ? '99+' : String(emailCount)) : null },
        { id: 'comms', label: 'Client Comms', icon: faInbox, to: '/client-comms', match: (p) => p === '/client-comms', show: hasFullAccess, badge: commsCount > 0 ? (commsCount > 99 ? '99+' : String(commsCount)) : null },
      ],
    },
    {
      label: 'Delivery',
      items: [
        { id: 'ideation', label: 'Ideation', icon: faLightbulb, to: '/ideation', match: (p) => p === '/ideation', show: hasFullAccess },
        { id: 'fulfillment', label: 'Fulfillment', icon: faClipboardCheck, to: '/fulfillment', match: (p) => p === '/fulfillment', show: hasFullAccess },
        { id: 'agents', label: 'Agents', icon: faFeather, to: '/agents', match: (p) => p === '/agents' || p.startsWith('/agents/'), show: isAdmin },
        { id: 'builds', label: 'Active Builds', icon: faHammer, to: '/builds', match: (p) => p === '/builds', show: hasFullAccess },
        { id: 'recurring', label: 'Recurring', icon: faRepeat, to: '/recurring', match: (p) => p === '/recurring', show: hasFullAccess },
        { id: 'content', label: 'Content', icon: faPenNib, to: '/content', match: (p) => p === '/content' || p.startsWith('/content/'), show: hasFullAccess },
      ],
    },
    {
      label: 'Sites',
      items: [
        // One item for the hosting area; HostingNav switches between Hosting, Uptime, Private Network and the Security views.
        { id: 'hosting', label: 'Hosting', icon: faServer, to: '/hosting', match: (p) => p.startsWith('/hosting') || p.startsWith('/uptime') || p.startsWith('/security') || p.startsWith('/private-network'), show: hasFullAccess, badge: taskCount > 0 ? 'Tasks' : null },
        // Client users only see the Private Network, so they keep it as its own item.
        { id: 'network', label: 'Private Network', icon: faNetworkWired, to: '/private-network', match: (p) => p.startsWith('/private-network'), show: !hasFullAccess, badge: taskCount > 0 ? 'Tasks' : null },
      ],
    },
  ];

  const visibleGroups = groups
    .map((g) => ({ ...g, items: g.items.filter((i) => i.show) }))
    .filter((g) => g.items.length > 0);

  const itemStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    height: '38px',
    // collapsed rail: 72px - 2×10px margin = 52px block; 16px pad centers the 20px icon
    padding: isExpanded ? '0 12px' : '0 16px',
    margin: '0 10px',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    background: isActive ? 'var(--surface-active)' : undefined,
    color: isActive ? 'var(--text-body)' : 'var(--text-muted)',
    fontSize: 'var(--text-sm)',
    fontWeight: (isActive ? 'var(--weight-medium)' : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
    cursor: 'pointer',
    width: 'calc(100% - 20px)',
    textAlign: 'left',
    position: 'relative',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textDecoration: 'none',
  });

  const labelStyle: React.CSSProperties = {
    opacity: isExpanded ? 1 : 0,
    transition: 'opacity var(--dur-base) var(--ease-out)',
    pointerEvents: 'none',
  };

  return (
    <>
      {/* Mobile overlay when sidebar is expanded */}
      {isExpanded && window.innerWidth >= 768 && (
        <div
          className="fixed inset-0 z-20 md:block lg:hidden"
          onClick={() => setIsExpanded(false)}
        ></div>
      )}

      <aside
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
        className="fixed left-0 top-0 bottom-0 z-30 overflow-hidden flex-col hidden md:flex"
        style={{
          width: isExpanded ? '232px' : '72px',
          background: 'var(--surface-sidebar)',
          borderRight: '1px solid var(--border-hairline)',
          transition: 'width var(--dur-slow) var(--ease-out)',
        }}
      >
        {/* Logo section */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '20px 0 14px 21px' }}>
          <Link to="/clients" style={{ display: 'inline-flex', flexShrink: 0 }}>
            <img src="/bullfinch-mark.svg" alt="Bullfinch" style={{ width: '30px', height: '30px' }} />
          </Link>
          <span
            className="ck-nav-label"
            style={{
              ...labelStyle,
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              letterSpacing: 'var(--tracking-tight)',
              color: 'var(--text-body)',
            }}
          >
            Cockpit
          </span>
        </div>

        {/* Menu Items */}
        <nav className="sidebar-menu" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden' }}>
          {visibleGroups.map((group, gi) => (
            <div key={group.label} style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: gi === 0 ? 0 : '14px' }}>
              {/* group label — fixed height so expand causes no vertical shift; collapsed shows a tiny centered rule */}
              <div style={{ position: 'relative', height: '22px', margin: '0 10px', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <span
                  className="t-eyebrow"
                  style={{
                    paddingLeft: '12px',
                    color: 'var(--text-faint)',
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity var(--dur-base) var(--ease-out)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {group.label}
                </span>
                <span
                  style={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '14px',
                    height: '1px',
                    background: 'var(--border-strong)',
                    opacity: isExpanded ? 0 : 1,
                    transition: 'opacity var(--dur-base) var(--ease-out)',
                  }}
                ></span>
              </div>
              {group.items.map((item) => {
                const isActive = item.match(location.pathname);
                return (
                  <Link
                    key={item.id}
                    to={item.to}
                    className="ck-nav-item hover-wash pressable"
                    style={itemStyle(isActive)}
                    onClick={() => window.innerWidth < 1024 && setIsExpanded(false)}
                  >
                    <FontAwesomeIcon icon={item.icon} style={{ fontSize: '15px', width: '20px', textAlign: 'center', flexShrink: 0 }} />
                    <span className="ck-nav-label" style={{ ...labelStyle, flex: 1 }}>{item.label}</span>
                    {item.badge && isExpanded ? (
                      <span
                        className="t-num"
                        style={{
                          ...labelStyle,
                          display: 'inline-flex',
                          alignItems: 'center',
                          height: '18px',
                          padding: '0 7px',
                          borderRadius: 'var(--radius-pill)',
                          background: 'var(--accent-soft)',
                          color: 'var(--accent-text)',
                          fontSize: 'var(--text-2xs)',
                          fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                          letterSpacing: 'var(--tracking-label)',
                          textTransform: 'uppercase',
                        }}
                      >
                        {item.badge}
                      </span>
                    ) : null}
                    {item.badge && !isExpanded ? (
                      <span
                        style={{
                          position: 'absolute',
                          top: '8px',
                          left: '26px',
                          width: '7px',
                          height: '7px',
                          borderRadius: 'var(--radius-pill)',
                          background: 'var(--accent)',
                        }}
                      ></span>
                    ) : null}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User and Actions section */}
        <div style={{ borderTop: '1px solid var(--border-hairline)', padding: '12px 0', display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0 8px 21px' }}>
              <Avatar name={user.name} src={avatarUrl} size={30} presence="online" />
              <span style={{ ...labelStyle, minWidth: 0 }}>
                <span className="ck-nav-label" style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'], color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name}</span>
                <span className="t-num" style={{ display: 'block', fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</span>
              </span>
            </div>
          )}

          <Link
            to="/settings"
            className="ck-nav-item hover-wash pressable"
            style={itemStyle(location.pathname.startsWith('/settings'))}
            onClick={() => window.innerWidth < 1024 && setIsExpanded(false)}
          >
            <FontAwesomeIcon icon={faGear} style={{ fontSize: '13px', width: '20px', textAlign: 'center', flexShrink: 0 }} />
            <span className="ck-nav-label" style={labelStyle}>Settings</span>
          </Link>
          <a
            href="#"
            onClick={handleLogout}
            className="ck-nav-item hover-wash pressable"
            style={{ ...itemStyle(false), color: 'var(--accent-text)' }}
          >
            <FontAwesomeIcon icon={faArrowRightFromBracket} style={{ fontSize: '13px', width: '20px', textAlign: 'center', flexShrink: 0 }} />
            <span className="ck-nav-label" style={labelStyle}>Logout</span>
          </a>
        </div>
      </aside>

      {/* Mobile toggle button - only show on tablet sizes */}
      <button
        className="fixed z-40 bottom-6 right-6 hidden md:block lg:hidden p-3 rounded-lg"
        style={{
          background: 'var(--surface-raised)',
          color: 'var(--text-body)',
          border: '1px solid var(--border-hairline)',
          boxShadow: 'var(--shadow-pop)',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <FontAwesomeIcon icon={isExpanded ? faArrowRightFromBracket : faChartPie} className="text-base" />
      </button>
    </>
  );
}
